import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  OutputEmitterRef,
  OutputRefSubscription,
  signal,
} from '@angular/core';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { Nullable } from '@core/helpers/nullable';
import { TuiExpand, TuiIcon, TuiButton } from '@taiga-ui/core';
import { RequestService } from '@core/services/request/request.service';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { TuiAccordion, TuiBadge } from '@taiga-ui/kit';
import { RequestProduct } from '@core/types/RequestProduct';
import { RequestFormGoodsItemComponent } from './components/request-form-goods-item';
import { BehaviorSubject } from 'rxjs';
import { IsLoadingService } from '@core/services';
import { RequestProductAndGoodsService } from '@core/services/request/request-product-and-goods.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { EmptyBlockComponent } from '@core/components/empty-block';
import { AccountService } from '@core/services/account';
import { getAlternateInstallerBannerState } from '@features/requests/request-form/helpers/alternate-installer-banner.helper';

@Component({
  selector: 'x-request-form-goods',
  imports: [
    TuiIcon,
    TuiExpand,
    AsyncPipe,
    RequestFormGoodsItemComponent,
    TuiBadge,
    TuiButton,
    TranslatePipe,
    NgClass,
    EmptyBlockComponent,
    NgTemplateOutlet,
    TuiAccordion,
  ],
  templateUrl: './request-form-goods.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormGoodsComponent implements OnInit, OnDestroy {
  $isInsideSummary = input<boolean>(false, {
    alias: 'isInsideSummary',
  });

  $completeAllProducts = input<OutputEmitterRef<void>>(undefined, {
    alias: 'completeAllProducts',
  });
  private readonly requestService = inject(RequestService);
  readonly permissionService = inject(OutkeepPermissionService);
  readonly isLoading$: BehaviorSubject<boolean> = inject(IsLoadingService);
  readonly $request = this.requestService.$request;
  readonly $currentUser = inject(AccountService).$currentUser;
  private readonly requestProductAndGoodsService = inject(
    RequestProductAndGoodsService,
  );
  private readonly destroyRef = inject(DestroyRef);
  readonly devicesProductsAndAddedDevicesAmountMismatchHintData = computed(() =>
    this.calculateDevicesAmountHint(),
  );
  readonly $alternateInstallerBanner = computed(() =>
    getAlternateInstallerBannerState(
      this.$request().products,
      this.$currentUser()?.personId?.id,
    ),
  );
  public $productCardExpanded = signal<Record<RequestProduct['id'], boolean>>(
    {},
  );

  public canCompleteSomeProducts = computed(() => {
    return (
      this.$request().products &&
      this.$request().products!.length > 1 &&
      this.$request().products!.some(product => !product.completed)
    );
  });

  private emitterSub: Nullable<OutputRefSubscription> = null;

  constructor() {
    effect(() => {
      const emitter = this.$completeAllProducts();

      if (emitter) {
        this.emitterSub = emitter.subscribe(() => {
          this.tryToCompleteAllProducts();
        });
      }
    });
  }

  ngOnDestroy(): void {
    if (this.emitterSub) {
      this.emitterSub.unsubscribe();
      this.emitterSub = null;
    }
  }

  ngOnInit(): void {
    this.$request().products?.forEach(product => {
      this.$productCardExpanded.update(products => ({
        ...products,
        [product.id]: false,
      }));
    });
  }

  private calculateDevicesAmountHint(): Nullable<{
    devicesAmountInRequest: number;
    deviceProductsAmountInRequest: number;
  }> {
    const devicesAmount = this.$request().devices?.length;

    if (!devicesAmount) {
      return null;
    }

    const products = this.$request().products || [];
    const onlyDeviceProducts = products.filter(
      product => product.requestProductId.deviceModel,
    );
    const deviceProductsAmount = onlyDeviceProducts.reduce(
      (total, deviceRequestProduct) => {
        return total + deviceRequestProduct.amount;
      },
      0,
    );

    if (deviceProductsAmount !== devicesAmount) {
      return {
        devicesAmountInRequest: devicesAmount,
        deviceProductsAmountInRequest: deviceProductsAmount,
      };
    }

    return null;
  }
  toggleExpandProductCard(id: RequestProduct['id']): void {
    this.$productCardExpanded.update(products => ({
      ...products,
      [id]: !products[id],
    }));
  }

  tryToCompleteAllProducts(): void {
    this.requestProductAndGoodsService
      .tryToCompleteAllProducts(this.$request().products)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }
}
